import type {
  WebsiteTemplateRegistryTemplateSummary,
  WebsiteUpsertRequest,
} from 'src/api/generated';

type WebsiteTemplatePreviewDescriptor = NonNullable<
  WebsiteTemplateRegistryTemplateSummary['preview']
>;
type WebsiteTemplatePreviewTheme = NonNullable<
  WebsiteTemplatePreviewDescriptor['defaultTheme']
>;
type WebsiteTemplatePreviewSampleProperty = NonNullable<
  WebsiteTemplatePreviewDescriptor['sampleProperties']
>[number];

export interface WebsiteTemplatePreviewProperty {
  title: string;
  operation: string;
  propertyType: string;
  location: string;
  price: string;
  bedrooms?: number;
  bathrooms?: number;
  parkingLots?: number;
  areaTotal?: string;
  imageUrl: string;
}

export interface WebsiteTemplatePreviewModel {
  renderer: string;
  layout: string;
  style: string;
  theme: Required<WebsiteTemplatePreviewTheme>;
  heroImageUrl: string;
  siteName: string;
  headline: string;
  description: string;
  whatsAppPhone: string;
  locations: string[];
  properties: WebsiteTemplatePreviewProperty[];
  badges: string[];
}

export interface WebsiteTemplatePreviewUrlParams {
  template?: WebsiteTemplateRegistryTemplateSummary;
  form: WebsiteUpsertRequest;
}

/**
 * Resolves a selectable template only when its complete persisted reference
 * matches the form.
 *
 * @param templates - Currently selectable registry templates.
 * @param form - Website draft carrying the selected template reference.
 * @returns The exact selectable template, when it remains selectable.
 */
export const resolveSelectedWebsiteTemplate = (
  templates: WebsiteTemplateRegistryTemplateSummary[],
  form: WebsiteUpsertRequest
): WebsiteTemplateRegistryTemplateSummary | undefined => {
  return templates.find(
    (template) =>
      template.key === form.templateKey &&
      template.version === form.templateVersion &&
      (!form.registryTemplateId ||
        template.registryTemplateId === form.registryTemplateId)
  );
};

const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

const FALLBACK_THEME: Required<WebsiteTemplatePreviewTheme> = {
  primaryColor: '#173f35',
  secondaryColor: '#2f3a34',
  accentColor: '#b9824b',
};

const FALLBACK_HERO_IMAGE_URL =
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1400&q=80';
const FALLBACK_PROPERTY_IMAGE_URL =
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=900&q=80';

const FALLBACK_PROPERTIES: WebsiteTemplatePreviewProperty[] = [
  {
    title: 'Casa familiar con jardin',
    operation: 'Venta',
    propertyType: 'Casa',
    location: 'Puerto Varas, Los Lagos',
    price: 'UF 9.800',
    bedrooms: 4,
    bathrooms: 3,
    parkingLots: 2,
    areaTotal: '220 m2',
    imageUrl: FALLBACK_PROPERTY_IMAGE_URL,
  },
  {
    title: 'Departamento conectado a servicios',
    operation: 'Arriendo',
    propertyType: 'Departamento',
    location: 'Providencia, Santiago',
    price: '$950.000',
    bedrooms: 2,
    bathrooms: 2,
    parkingLots: 1,
    areaTotal: '82 m2',
    imageUrl:
      'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&w=900&q=80',
  },
];

const FALLBACK_LOCATIONS = ['Puerto Varas', 'Valdivia', 'Santiago'];
const FALLBACK_BADGES = [
  'Busqueda avanzada',
  'Catalogo responsive',
  'WhatsApp integrado',
];

const MAX_MOCK_PREVIEW_URL_LENGTH = 8_192;

const isValidHexColor = (value?: string): value is string =>
  Boolean(value && HEX_COLOR_PATTERN.test(value));

const resolveColor = (
  formValue: string | undefined,
  descriptorValue: string | undefined,
  fallbackValue: string
): string => {
  if (isValidHexColor(formValue)) return formValue;
  if (isValidHexColor(descriptorValue)) return descriptorValue;
  return fallbackValue;
};

const resolveText = (...values: (string | undefined)[]): string =>
  values.find((value) => value?.trim())?.trim() ?? '';

const normalizeAreaTotal = (
  value: WebsiteTemplatePreviewSampleProperty['areaTotal']
): string | undefined => {
  if (typeof value === 'number') return `${value} m2`;
  return value;
};

const normalizeProperty = (
  property: WebsiteTemplatePreviewSampleProperty
): WebsiteTemplatePreviewProperty => ({
  title: property.title,
  operation: property.operation,
  propertyType: property.propertyType,
  location: property.location,
  price: property.price,
  bedrooms: property.bedrooms,
  bathrooms: property.bathrooms,
  parkingLots: property.parkingLots,
  areaTotal: normalizeAreaTotal(property.areaTotal),
  imageUrl: property.imageUrl ?? FALLBACK_PROPERTY_IMAGE_URL,
});

const getPreviewProperties = (
  descriptor?: WebsiteTemplatePreviewDescriptor
): WebsiteTemplatePreviewProperty[] => {
  const sampleProperties = descriptor?.sampleProperties ?? [];
  const normalizedProperties = sampleProperties
    .filter((property) => property.title && property.price)
    .map(normalizeProperty);

  return normalizedProperties.length > 0
    ? normalizedProperties
    : FALLBACK_PROPERTIES;
};

const setOptionalTextSearchParam = (
  url: URL,
  key: string,
  value: string | undefined
): void => {
  const normalizedValue = value?.trim();
  if (normalizedValue) {
    url.searchParams.set(key, normalizedValue);
  }
};

export const buildWebsiteTemplatePreviewModel = (
  template: WebsiteTemplateRegistryTemplateSummary | undefined,
  form: WebsiteUpsertRequest
): WebsiteTemplatePreviewModel => {
  const preview = template?.preview;
  const formTheme = form.draftConfig.theme;
  const formContent = form.draftConfig.content;

  return {
    renderer: preview?.renderer ?? template?.key ?? form.templateKey,
    layout: preview?.layout ?? 'search-first',
    style: preview?.style ?? 'southern-premium',
    theme: {
      primaryColor: resolveColor(
        formTheme?.primaryColor,
        preview?.defaultTheme?.primaryColor,
        FALLBACK_THEME.primaryColor
      ),
      secondaryColor: resolveColor(
        formTheme?.secondaryColor,
        preview?.defaultTheme?.secondaryColor,
        FALLBACK_THEME.secondaryColor
      ),
      accentColor: resolveColor(
        formTheme?.accentColor,
        preview?.defaultTheme?.accentColor,
        FALLBACK_THEME.accentColor
      ),
    },
    heroImageUrl: resolveText(
      formTheme?.heroImageUrl,
      preview?.heroImageUrl,
      FALLBACK_HERO_IMAGE_URL
    ),
    siteName: resolveText(
      formContent.siteName,
      template?.name,
      'Corredora del Sur'
    ),
    headline: resolveText(
      formContent.headline,
      template?.description,
      'Encuentra propiedades con asesoria experta'
    ),
    description: resolveText(
      formContent.description,
      template?.description,
      'Compra, venta y arriendo con una experiencia clara para tus clientes.'
    ),
    whatsAppPhone: form.draftConfig.whatsApp.phoneNumber,
    locations:
      preview?.sampleLocations && preview.sampleLocations.length > 0
        ? preview.sampleLocations
        : FALLBACK_LOCATIONS,
    properties: getPreviewProperties(preview),
    badges:
      preview?.badges && preview.badges.length > 0
        ? preview.badges
        : FALLBACK_BADGES,
  };
};

export const buildWebsiteTemplatePreviewUrl = ({
  template,
  form,
}: WebsiteTemplatePreviewUrlParams): string | undefined => {
  if (!template?.mockPreviewUrl) {
    return undefined;
  }

  const url = new URL(template.mockPreviewUrl);
  const content = form.draftConfig.content;
  const theme = form.draftConfig.theme;
  const whatsApp = form.draftConfig.whatsApp;

  url.searchParams.set('mockPreview', '1');
  url.searchParams.set('siteName', content.siteName);
  if (content.headline) {
    url.searchParams.set('headline', content.headline);
  }
  if (content.description) {
    url.searchParams.set('description', content.description);
  }
  setOptionalTextSearchParam(url, 'address', content.address);
  setOptionalTextSearchParam(url, 'email', content.email);
  setOptionalTextSearchParam(url, 'phone', content.phone);
  setOptionalTextSearchParam(url, 'businessHours', content.businessHours);
  setOptionalTextSearchParam(url, 'instagramUrl', content.instagramUrl);
  setOptionalTextSearchParam(url, 'facebookUrl', content.facebookUrl);
  setOptionalTextSearchParam(url, 'tiktokUrl', content.tiktokUrl);
  if (whatsApp.phoneNumber) {
    url.searchParams.set('whatsAppPhone', whatsApp.phoneNumber);
  }
  if (theme?.primaryColor) {
    url.searchParams.set('primaryColor', theme.primaryColor);
  }
  if (theme?.secondaryColor) {
    url.searchParams.set('secondaryColor', theme.secondaryColor);
  }
  if (theme?.accentColor) {
    url.searchParams.set('accentColor', theme.accentColor);
  }
  if (theme?.heroImageUrl) {
    url.searchParams.set('heroImageUrl', theme.heroImageUrl);
  }
  if (theme?.logoUrl) {
    url.searchParams.set('logoUrl', theme.logoUrl);
  }

  const serializedUrl = url.toString();
  return serializedUrl.length <= MAX_MOCK_PREVIEW_URL_LENGTH
    ? serializedUrl
    : undefined;
};
